import {
  Banner,
  Button,
  Input,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  useApiMutation,
  useIntercom,
  useInvalidateHasuraQueries,
  useQuery,
} from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarnNotification,
} from '@vrplatform/theme';
import { hasValue, toTitleCase } from '@vrplatform/utils';
import { Box, Collapse, Group, Modal } from '@mantine/core';
import type {
  accountAssignmentType_enum,
  accountClassification_enum,
  accountType_enum,
} from '@vrplatform/graphql';
import { formatAccountType } from '@vrplatform/ui-common';
import { useEffect } from 'react';
import {
  Controller,
  type UseFormReturn,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAccountsAssignmentsQuery } from '../ledger-accounts/useAccountsAssignmentsQuery';

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

type Props = {
  opened: boolean;
  closeModal: () => void;
  onAccountAdded?: (accountId: string) => void;
  initialValues?: AddAccountModalInitialValues;
};

type FormInputs = {
  title: string;
  classification: accountClassification_enum | undefined;
  assignments: accountAssignmentType_enum[];
  type: accountType_enum;
  categoryId: string | undefined;
};

export type AddAccountModalInitialValues = Partial<FormInputs>;

const DEFAULT_FORM_VALUES: FormInputs = {
  assignments: [],
  categoryId: undefined,
  classification: undefined,
  title: '',
  type: 'ledger',
};

const getFormValues = (
  initialValues?: AddAccountModalInitialValues
): FormInputs => ({
  ...DEFAULT_FORM_VALUES,
  ...initialValues,
  assignments: initialValues?.assignments ?? DEFAULT_FORM_VALUES.assignments,
});

export const STORYBOOK_ADD_ACCOUNT_CLASSIFICATIONS_QUERY_KEY =
  'add-account-modal-classifications';

export const getStorybookAddAccountCategoriesQueryKey = (
  classification?: accountClassification_enum
) => `add-account-modal-categories-${classification ?? 'unset'}`;

const TYPES: SelectItem<accountType_enum>[] = [
  {
    value: 'ledger',
    label: formatAccountType('ledger'),
    description: 'Posts to your ledger and affects balances',
  },
  {
    value: 'bank',
    label: formatAccountType('bank'),
    description: 'Real bank/card account used for reconciliation',
  },
  {
    value: 'nonPosting',
    label: formatAccountType('nonPosting'),
    description:
      'Informational only, typically used in fee templates. Does not affect ledger balances',
  },
];

export const AddAccountModal = ({
  opened,
  closeModal: cl,
  onAccountAdded,
  initialValues,
}: Props) => {
  const intercom = useIntercom();
  const goto = useNavigate();

  const methods = useForm<FormInputs>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    if (!opened) return;

    methods.reset(getFormValues(initialValues));
  }, [initialValues, methods, opened]);

  const invalidate = useInvalidateHasuraQueries([
    'accounts',
    'settings',
    'paymentLineClassifications',
  ]);

  const { mutateAsync: insert } = useApiMutation('post', '/accounts');

  const closeModal = () => {
    methods.reset(DEFAULT_FORM_VALUES);
    cl();
  };

  const type = useWatch({
    control: methods.control,
    name: 'type',
  });
  const classification = useWatch({
    control: methods.control,
    name: 'classification',
  });

  const disabled = type === 'bank';

  const submit = async (data: FormInputs) => {
    if (!data.title.trim())
      return showWarnNotification({
        message: 'Please enter an account title.',
      });

    const assignments = data.assignments?.filter(hasValue) ?? [];

    return await insert({
      body: {
        name: data.title.trim(),
        type: data.type ?? 'ledger',
        status: 'active',
        assignments: type === 'nonPosting' ? [] : assignments,
        categoryId: data.categoryId,
      },
    })
      .then((res) => {
        invalidate();
        showSuccessNotification({
          title: 'Accounts',
          message: 'Account was added successfully',
        });
        closeModal();
        onAccountAdded?.(res.data.id);
      })
      .catch((err) => {
        showErrorNotification({
          title: 'Failed to add account',
          message:
            err.message || 'Please reach out to support if the issue persists',
        });
      });
  };

  const {
    data: availableAssignmentTypes,
    isLoading: loadingAvailableAssignmentTypes,
    error: errorAvailableAssignmentTypes,
  } = useAccountsAssignmentsQuery({
    classification,
    skip: !opened,
    currentAccountAssignments: initialValues?.assignments ?? [],
  });

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title="Add an account"
      closeButtonProps={{
        'aria-label': 'Close add account modal',
      }}
      styles={{
        title: { fontWeight: 500 },
      }}
      centered
      size={520}
      withinPortal
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Controller
          control={methods.control}
          rules={{
            required: 'Type is required',
          }}
          name="type"
          defaultValue="ledger"
          render={({ field, fieldState }) => {
            const value = TYPES?.find((x) => x.value === field.value) ?? null;

            return (
              <InputWrapper
                label="Type"
                error={fieldState.error?.message}
                required
              >
                <InputSelect
                  type="single"
                  data={{
                    options: TYPES,
                  }}
                  value={value}
                  setValue={(v) => {
                    v?.value && field.onChange(v?.value);
                  }}
                  inputProps={{
                    error: !!fieldState.error,
                  }}
                  dropdownProps={{
                    withinPortal: true,
                    width: 'target',
                  }}
                />
              </InputWrapper>
            );
          }}
        />

        <Box
          component="fieldset"
          disabled={disabled}
          p={0}
          sx={(theme) => ({
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing.md,
          })}
        >
          <Controller
            control={methods.control}
            rules={{
              required: 'Title is required',
              validate: trapSpacesForRequiredFields,
            }}
            name="title"
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Name"
                error={fieldState.error?.message}
                required
                sx={{
                  flex: 1,
                }}
              >
                <Input
                  ref={field.ref}
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={!!fieldState.error}
                  placeholder="E.g. Management fees"
                  disabled={field.disabled}
                />
              </InputWrapper>
            )}
          />

          <Group align="flex-start">
            <AccountClassificationInput methods={methods} disabled={disabled} />
            <AccountCategoryInput methods={methods} disabled={disabled} />
          </Group>

          <Controller
            control={methods.control}
            name="assignments"
            defaultValue={[]}
            disabled={disabled || type === 'nonPosting'}
            rules={{
              required: false,
            }}
            render={({ field, fieldState }) => {
              const value =
                availableAssignmentTypes?.filter((x) =>
                  field.value?.includes(x.value)
                ) || [];

              return (
                <InputWrapper
                  label="Assignments"
                  error={fieldState.error?.message}
                  mb="sm"
                >
                  <InputSelect
                    type="multiple"
                    value={value}
                    setValue={(v) => field.onChange(v.map((x) => x.value))}
                    data={{
                      options: availableAssignmentTypes || [],
                      loading: loadingAvailableAssignmentTypes,
                      error: errorAvailableAssignmentTypes,
                    }}
                    inputProps={{
                      placeholder: 'Select assignments',
                      withClearButton: true,
                      disabled: field.disabled,
                    }}
                    dropdownProps={{
                      withinPortal: true,
                    }}
                  />
                </InputWrapper>
              );
            }}
          />
        </Box>

        <Collapse in={disabled}>
          <Banner
            mounted={true}
            type="info"
            title="Trust reconciliation"
            message="Use reconciliation to create and manage your trust accounts."
            icon="InfoIcon"
            size="sm"
            action={{
              label: 'Learn more',
              onClick: () => {
                goto('/reconciliation');
                intercom.showArticle(11190163);
              },
            }}
          />
        </Collapse>

        <Group justify="right">
          <Button
            type="button"
            onClick={() => closeModal()}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={disabled}
            onClick={methods.handleSubmit(submit, (err) => console.error(err))}
            loading={methods.formState.isSubmitting}
          >
            Add account
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export const AccountCategoryInput = ({
  methods,
  disabled,
}: {
  methods: UseFormReturn<FormInputs>;
  disabled?: boolean;
}) => {
  const classification = useWatch({
    control: methods.control,
    name: 'classification',
  });

  const queryData = useQuery(
    (q, args) => {
      if (!args.classification) return [];

      return q
        .accountCategories({
          order_by: [
            {
              name: 'asc_nulls_last',
            },
          ],
          where: {
            classification: { _eq: args.classification },
          },
        })
        .map((x) => ({
          value: x.id,
          label: x.name || '-',
        }));
    },
    {
      storybookQueryKey:
        getStorybookAddAccountCategoriesQueryKey(classification),
      variables: {
        classification,
      },
    }
  );

  return (
    <Controller
      control={methods.control}
      name="categoryId"
      rules={{
        required: !classification ? false : 'Please select a category',
      }}
      disabled={disabled}
      render={({ field, fieldState }) => {
        const value =
          queryData.data?.find((x) => x.value === field.value) ?? null;

        return (
          <InputWrapper
            label="Category"
            error={fieldState.error?.message}
            flex={1}
            required={!!classification}
          >
            <InputSelect
              type="single"
              data={{
                options: queryData.data || [],
                loading: queryData.isLoading,
                error: queryData.error,
              }}
              value={value}
              setValue={(v) => field.onChange(v?.value)}
              inputProps={{
                error: !!fieldState.error,
                placeholder: 'Select category',
                withClearButton: true,
                disabled: !classification || field.disabled,
              }}
              dropdownProps={{
                withinPortal: true,
                width: 'target',
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};

export const AccountClassificationInput = ({
  methods,
  disabled,
}: {
  methods: UseFormReturn<FormInputs>;
  disabled?: boolean;
}) => {
  const queryData = useQuery(
    (q) => {
      return q
        .accountClassifications({
          order_by: [{ name: 'asc_nulls_last' }],
        })
        .map((clas) => ({
          label: toTitleCase(clas.name)!,
          value: clas.name!,
        }));
    },
    {
      storybookQueryKey: STORYBOOK_ADD_ACCOUNT_CLASSIFICATIONS_QUERY_KEY,
      variables: {},
    }
  );

  return (
    <Controller
      control={methods.control}
      rules={{
        required: 'Classification is required',
      }}
      name="classification"
      render={({ field, fieldState }) => {
        const value =
          queryData.data?.find((x) => x.value === field.value) ?? null;

        return (
          <InputWrapper
            label="Classification"
            error={fieldState.error?.message}
            required
          >
            <InputSelect
              type="single"
              data={{
                options: queryData.data || [],
                loading: queryData.isLoading,
                error: queryData.error,
              }}
              value={value}
              setValue={(v) => {
                field.onChange(v?.value);
                methods.setValue('categoryId', undefined);
              }}
              inputProps={{
                error: !!fieldState.error,
                placeholder: 'Select classification',
                withClearButton: true,
                width: 200,
                disabled: field.disabled || disabled,
              }}
              dropdownProps={{
                withinPortal: true,
                width: 'target',
              }}
            />
          </InputWrapper>
        );
      }}
    />
  );
};
